import { ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { motion } from 'framer-motion';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useFormState } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import { FormError } from 'shared/components/FormControl';

import Form from 'dashboard/layouts/FormLayout/Form';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { FREE_EMAIL_PROVIDER_REGEX } from 'lib/constants/regexes';
import { captureMessage } from 'lib/reporting';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormRegisterEmailProps {
  savedEmail: string;
  submit: (data: FormRegisterEmailData) => void;
}

export const FormRegisterEmail = (props: FormRegisterEmailProps) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY || ''}>
      <FormRegisterEmailContents {...props} />
    </GoogleReCaptchaProvider>
  );
};

const FormRegisterEmailContents = ({ savedEmail, submit }: FormRegisterEmailProps) => {
  const dispatch = useDispatch();

  const { control, getValues, handleSubmit, register, setValue } =
    useFormSecurely<FormRegisterEmailData>({
      defaultValues: {
        'email-address': savedEmail,
        'recaptcha-token': null,
      },
    });
  const { isSubmitting, errors } = useFormState({
    control,
  });

  // Email must be trimmed before running validation
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('email-address', event.target.value.trim());
  };

  const { executeRecaptcha } = useGoogleReCaptcha();
  register('recaptcha-token');
  const RECAPTCHA_ACTION_NAME = 'dashboard_signup';

  const submitForm = async () => {
    let recaptchaToken = null;

    if (!executeRecaptcha) {
      dispatch(showMessage('An error occurred. Please try again.', MessageType.Failure));
      captureMessage('executeRecaptcha failed to load', 'warning');
      return;
    }

    // Note: We generate the reCAPTCHA token here instead of when the page loads, because they only
    // stay valid for two minutes. The tradeoff is that the user has to wait for the token to be
    // generated after they submit the form. In the future, we could generate the token as soon as
    // `executeRecaptcha` is available and store it in state with a timestamp, so that we only
    // regenerate the token when the user submits the form if the token is about to expire.
    recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION_NAME);

    if (!recaptchaToken) {
      dispatch(showMessage('An error occurred. Please try again.', MessageType.Failure));
      captureMessage('recaptchaToken failed to generate', 'warning');
      return;
    }

    setValue('recaptcha-token', recaptchaToken);

    // Ensures form is submitted with updated values
    const updatedData = getValues();
    submit(updatedData);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
      <Form onSubmit={handleSubmit(submitForm)}>
        <Label htmlFor='email-address' size='md' weight='bold'>
          Business email address
        </Label>
        <Spacer height={1} />
        {errors['email-address'] && <FormError>{errors['email-address'].message}</FormError>}
        <Input
          {...register('email-address', {
            required: 'Please enter a business email address',
            pattern: {
              value: FREE_EMAIL_PROVIDER_REGEX,
              message: 'Please enter a valid business email address.',
            },
          })}
          id='email-address'
          data-test='form-register__email'
          type='text'
          onChange={handleEmailChange}
        />
        <Spacer height={2} />

        <Button
          type='submit'
          suffixIcon={<ArrowRightIcon />}
          disabled={isSubmitting}
          isPending={isSubmitting}
          data-test='form-register__submit'
        >
          Continue
        </Button>
        <Spacer height={1} />

        <Paragraph size='sm'>
          By clicking Continue, I agree to Persona’s{' '}
          <a href='https://withpersona.com/legal/privacy-policy' target='_blank' rel='noreferrer'>
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href='https://withpersona.com/legal/terms-of-use' target='_blank' rel='noreferrer'>
            Terms of Use
          </a>
          .
        </Paragraph>
        <Spacer height={4} />

        <Paragraph size='md'>
          Already have an account? <Link to='/dashboard/login'>Sign in</Link>
        </Paragraph>
        <Spacer height={1} />
        <Paragraph size='md'>
          Trying to verify your identity?{' '}
          <Link to='/dashboard/contact-us?category=end-user'>Get support</Link>
        </Paragraph>
      </Form>
    </motion.div>
  );
};

export interface FormRegisterEmailData {
  'email-address': string;
  'recaptcha-token': string | null;
}

export default FormRegisterEmail;
